import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  CheckBadgeIcon,
  BuildingOffice2Icon,
  UserIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import { Invoice } from '../../types/invoice';
import {
  getInvoiceById,
  formatInvoiceDate,
  getTxUrl,
  truncateAddress,
  truncateTxHash,
  generatePDF,
} from '../../services/invoiceService';

const InvoiceDetailPage: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvoice = async () => {
      if (invoiceId) {
        const data = await getInvoiceById(invoiceId);
        setInvoice(data);
      }
      setLoading(false);
    };
    fetchInvoice();
  }, [invoiceId]);

  const handleDownloadPDF = async () => {
    if (invoice) {
      const blob = await generatePDF(invoice);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoiceNumber}.txt`; // In production, this would be .pdf
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-16">
        <DocumentTextIcon className="w-16 h-16 text-gray-700 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Factura no encontrada</h3>
        <p className="text-gray-400 mb-6">La factura que buscas no existe o no tienes acceso a ella</p>
        <Link
          to="/dashboard/invoices"
          className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
        >
          Volver a Facturas
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard/invoices"
            className="p-2 hover:bg-dark-800 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-400" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <DocumentTextIcon className="w-7 h-7 text-primary-400" />
              Factura {invoice.invoiceNumber}
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              {formatInvoiceDate(invoice.invoiceDate)}
            </p>
          </div>
        </div>
        <button
          onClick={handleDownloadPDF}
          className="flex items-center gap-2 px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
          Descargar PDF
        </button>
      </div>

      {/* Invoice Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-dark-800/50 rounded-xl border border-dark-700 overflow-hidden"
      >
        {/* Invoice Header */}
        <div className="bg-gradient-to-r from-primary-500/20 to-purple-500/20 p-6 border-b border-dark-700">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-3xl font-bold text-white">FACTURA</h2>
              <p className="text-gray-400 mt-1">{invoice.company.legalName}</p>
            </div>
            <div className="text-right">
              <p className="text-white font-mono text-lg">{invoice.invoiceNumber}</p>
              <div className="flex items-center gap-2 mt-2 text-green-400">
                <CheckBadgeIcon className="w-5 h-5" />
                <span className="text-sm">Completada</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Seller & Buyer Info */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Company Info */}
            <div className="bg-dark-900/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3 text-gray-400">
                <BuildingOffice2Icon className="w-5 h-5" />
                <span className="text-sm font-medium">Emisor</span>
              </div>
              <p className="text-white font-semibold">{invoice.company.legalName}</p>
              <p className="text-gray-400 text-sm">CIF: {invoice.company.cif}</p>
              <p className="text-gray-400 text-sm mt-2">{invoice.company.address}</p>
              <p className="text-gray-400 text-sm">
                {invoice.company.postalCode} {invoice.company.city}, {invoice.company.country}
              </p>
              <p className="text-gray-400 text-sm mt-2">{invoice.company.email}</p>
            </div>

            {/* Buyer Info */}
            <div className="bg-dark-900/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3 text-gray-400">
                <UserIcon className="w-5 h-5" />
                <span className="text-sm font-medium">Comprador</span>
              </div>
              <p className="text-white font-semibold">{invoice.buyerName || 'Usuario Anónimo'}</p>
              <p className="text-gray-400 text-sm font-mono mt-1">
                {truncateAddress(invoice.buyerAddress)}
              </p>
              {invoice.buyerEmail && (
                <p className="text-gray-400 text-sm mt-2">{invoice.buyerEmail}</p>
              )}
            </div>
          </div>

          {/* Item Details */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <CubeIcon className="w-5 h-5 text-primary-400" />
              Detalle del Item
            </h3>
            <div className="bg-dark-900/50 rounded-lg overflow-hidden">
              <div className="flex items-center gap-4 p-4">
                <img
                  src={invoice.itemImage}
                  alt={invoice.itemName}
                  className="w-20 h-20 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <p className="text-white font-semibold">{invoice.itemName}</p>
                  <p className="text-gray-400 text-sm">{invoice.itemCollection}</p>
                  <p className="text-gray-500 text-xs mt-1">Subasta: {invoice.auctionType}</p>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold text-xl">{invoice.priceCrypto} {invoice.currency}</p>
                  <p className="text-gray-400 text-sm">~€{invoice.priceEur.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing Summary */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Resumen</h3>
            <div className="bg-dark-900/50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between text-gray-400">
                <span>Subtotal</span>
                <span>{invoice.priceCrypto} {invoice.currency}</span>
              </div>
              <div className="flex justify-between text-gray-400">
                <span>Comisión plataforma ({invoice.feePercentage}%)</span>
                <span>{invoice.feeAmount.toFixed(4)} {invoice.currency}</span>
              </div>
              <div className="border-t border-dark-700 pt-3 flex justify-between">
                <span className="text-white font-semibold">Total</span>
                <div className="text-right">
                  <p className="text-white font-bold text-lg">{invoice.totalCrypto} {invoice.currency}</p>
                  <p className="text-gray-400 text-sm">~€{invoice.totalEur.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Blockchain Transaction */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Transacción Blockchain</h3>
            <div className="bg-dark-900/50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Hash de Transacción</span>
                <div className="flex items-center gap-2">
                  <code className="text-primary-400 text-sm">{truncateTxHash(invoice.txHash)}</code>
                  <a
                    href={getTxUrl(invoice.txHash)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 hover:bg-dark-700 rounded"
                    title="Ver en Polygonscan"
                  >
                    <ArrowTopRightOnSquareIcon className="w-4 h-4 text-gray-400" />
                  </a>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Red</span>
                <span className="text-white">{invoice.network} (Chain ID: {invoice.chainId})</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Bloque</span>
                <span className="text-white">{invoice.blockNumber.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Vendedor</span>
                <span className="text-white font-mono text-sm">{truncateAddress(invoice.sellerAddress)}</span>
              </div>
            </div>
          </div>

          {/* Footer Note */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-blue-300 text-sm">
              Esta factura es un comprobante de transacción blockchain. Los datos son inmutables
              y verificables on-chain a través de{' '}
              <a
                href={getTxUrl(invoice.txHash)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:underline"
              >
                Polygonscan
              </a>.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default InvoiceDetailPage;
